import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function useLastAllowedPath({
  allowedPrefixes,
  accessLoaded,
  guestAllowedPages,
  userAllowedPages,
  hasAccessEnabled,
  isAdmin,
  fallbackAllowedPath,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const [showAccessDenied, setShowAccessDenied] = React.useState(false);
  const [lastDeniedPath, setLastDeniedPath] = React.useState('');
  const [lastAllowedPath, setLastAllowedPath] = React.useState('/dashboard/personal');
  const [initializedLastAllowed, setInitializedLastAllowed] = React.useState(false);

  const getFullPath = React.useCallback(() => {
    const search = location.search || '';
    const hash = location.hash || '';
    return `${location.pathname}${search}${hash}`;
  }, [location.pathname, location.search, location.hash]);

  const shouldRedirectOnDenied = !(!hasAccessEnabled && !isAdmin);

  const handleDenied = React.useCallback((path) => {
    setShowAccessDenied(true);
    setLastDeniedPath(path);
  }, []);

  // Track last allowed page globally (for safety if any unguarded navigation happens)
  React.useEffect(() => {
    // Initialize last allowed path from session or current path on first run
    if (!initializedLastAllowed) {
      const stored = sessionStorage.getItem('lastAllowedPath');
      const currentFull = getFullPath();
      setLastAllowedPath(stored || currentFull || fallbackAllowedPath);
      setInitializedLastAllowed(true);
      if (!stored) sessionStorage.setItem('lastAllowedPath', currentFull || fallbackAllowedPath);
    }

    // If access rules not loaded yet, do not update lastAllowedPath or bounce
    const accessReady =
      accessLoaded &&
      (Array.isArray(guestAllowedPages) ? true : true) &&
      (userAllowedPages !== null || Array.isArray(guestAllowedPages));
    if (!accessReady) return;

    const lower = (location.pathname || '').toLowerCase();
    const allowed = allowedPrefixes.some((prefix) => {
      const pl = (prefix || '').toLowerCase();
      return lower === pl || lower.startsWith(pl + '/');
    });

    if (allowed) {
      const full = getFullPath();
      if (lastAllowedPath !== full) {
        setLastAllowedPath(full);
        sessionStorage.setItem('lastAllowedPath', full);
      }
    } else {
      const full = getFullPath();
      if (lastDeniedPath !== full) {
        // Special handling for ex-employees - they should stay on the access denied page
        if (!hasAccessEnabled && !isAdmin) {
          setShowAccessDenied(true);
          setLastDeniedPath(full);
          // Don't redirect ex-employees, let them see the access denied message
        } else {
          setShowAccessDenied(true);
          setLastDeniedPath(full);
          const dest = lastAllowedPath || sessionStorage.getItem('lastAllowedPath') || fallbackAllowedPath;
          if (dest && full !== dest) {
            navigate(dest, { replace: true });
          }
        }
      }
    }
  }, [
    location.pathname,
    location.search,
    location.hash,
    allowedPrefixes,
    lastAllowedPath,
    lastDeniedPath,
    navigate,
    getFullPath,
    accessLoaded,
    initializedLastAllowed,
    guestAllowedPages,
    userAllowedPages,
    fallbackAllowedPath,
    hasAccessEnabled,
    isAdmin,
  ]);

  return {
    showAccessDenied,
    setShowAccessDenied,
    lastDeniedPath,
    lastAllowedPath,
    setLastAllowedPath,
    getFullPath,
    handleDenied,
    shouldRedirectOnDenied,
  };
}


